import { Link } from "react-router-dom"
import { ChevronRight, ExternalLink, Globe, Trash2 } from "lucide-react"
import { Integration } from "../../../types"
import { Tabs } from "../../../common/ui"
import { DefaultBtn, BtnDanger } from "../../../common/ui/buttons"
import IntegrationStatusTag from "../../../common/ui/integrations/table/components/IntegrationStatusTag"

const TYPE_LABELS: Record<string, string> = {
  standalone: "Standalone embed",
  boomtown: "LO page embed",
  api: "API embed",
  chatbot: "Website chat embed"
}

const TABS = [
  { id: "settings", label: "Settings" },
  { id: "status", label: "Status" },
  { id: "install", label: "Install" },
  { id: "troubleshoot", label: "Troubleshoot" }
]

interface IntegrationHeaderProps {
  integration: Integration | undefined
  isLoading: boolean
  id: string
  tab: string
  onTabChange: (tab: string) => void
  onClickPreview: () => void
  onDeleteClick: () => void
}

const IntegrationHeader = ({
  integration,
  isLoading,
  id,
  tab,
  onTabChange,
  onClickPreview,
  onDeleteClick
}: IntegrationHeaderProps) => {
  const typeLabel =
    TYPE_LABELS[integration?.type ?? ""] ?? integration?.type ?? ""

  return (
    <div className="bg-white border-b border-neutral-200 px-4 pt-4 sm:px-6 sm:pt-6">
      <div className="flex items-center gap-1.5 text-sm text-neutral-400 mb-4">
        <Link
          to="/integrations"
          className="text-primary-600 font-medium hover:text-primary-700"
        >
          Integrations
        </Link>
        <ChevronRight size={12} className="text-neutral-400" />
        {isLoading ? (
          <div className="h-3.5 w-28 animate-pulse rounded bg-gray-200 sm:w-48" />
        ) : (
          <span className="truncate text-neutral-900 font-medium">
            {integration?.title}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-4 mb-2 sm:flex-row sm:items-start sm:justify-between sm:mb-6">
        <div className="min-w-0">
          {isLoading ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-6 w-40 animate-pulse rounded bg-gray-200 sm:w-64" />
                <div className="h-6 w-16 animate-pulse rounded-full bg-gray-200" />
              </div>
              <div className="h-4 w-72 max-w-full animate-pulse rounded bg-gray-200 sm:w-96" />
              <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-1.5 sm:gap-2.5">
                <h1 className="text-xl font-bold text-neutral-900">
                  {integration?.title}
                </h1>
                <IntegrationStatusTag integrationId={id} />
              </div>

              <div className="flex flex-wrap items-center gap-1.5 text-sm text-neutral-400">
                {typeLabel && (
                  <span className="text-neutral-600">{typeLabel}</span>
                )}
                {typeLabel && integration?.app_key && <span>·</span>}
                {integration?.app_key && (
                  <span className="text-neutral-600">
                    App key:{" "}
                    <code className="font-mono text-xs bg-neutral-100 px-1.5 py-0.5 rounded">
                      {integration.app_key}
                    </code>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 mt-1 text-sm text-neutral-400">
                <Globe size={12} className="shrink-0" />
                <a
                  href={integration?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-primary-600 font-medium hover:text-primary-700"
                >
                  {integration?.url}
                </a>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {isLoading ? (
            <>
              <div className="h-9 w-24 animate-pulse rounded-md border border-gray-100 bg-gray-100" />
              <div className="h-9 w-40 animate-pulse rounded-md border border-gray-100 bg-gray-100" />
            </>
          ) : (
            <>
              <DefaultBtn size="sm" onClick={onClickPreview}>
                <ExternalLink size={13} className="mr-1.5" />
                Preview
              </DefaultBtn>
              <BtnDanger size="sm" onClick={onDeleteClick}>
                <Trash2 size={13} className="mr-1.5" />
                Delete Integration
              </BtnDanger>
            </>
          )}
        </div>
      </div>

      <Tabs currentTab={tab} tabs={TABS} onChange={onTabChange} />
    </div>
  )
}

export default IntegrationHeader
